/* eslint-disable no-unused-vars */
/* eslint-disable*/
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { ReportsService } from "../../../../services/api/reports";
import { downloadEwayBillReport } from "../../../../utils/downloadUtils";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import Heading from "../../../../components/Heading";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import {
    ZONE_OPTIONS,
    REPORT_PAGINATION_DEFAULTS,
    REPORT_FILTER_LABELS,
    REPORT_PLACEHOLDERS,
    REPORT_DOWNLOAD_TEXT,
    REPORT_ERROR_MESSAGES,
    BOOKING_TYPE_OPTIONS
} from "../../../../constants/reportConstants";
import "./styles.scss";

const EwayBillReport = () => {
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cpLoading, setCpLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [selectedCPs, setSelectedCPs] = useState([]);
    const [selectedZones, setSelectedZones] = useState([]);
    const [selectedBookingTypes, setSelectedBookingTypes] = useState([]);
    const [pagination, setPagination] = useState({
        ...REPORT_PAGINATION_DEFAULTS
    });
    const [reportData, setReportData] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [hubUser, setHubUser] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Eway Bill Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    // Request cancellation hook
    const { createNewRequest, isLatestRequest, cancelPreviousRequest } = useRequestCancellation();


    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userid = JSON.parse(localStorage.getItem("userId") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            
            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedHubs([]);
            } else {
                setSelectedHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    // Fetch hub list on component mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch {
                message.error(REPORT_ERROR_MESSAGES.FETCH_HUB_LIST_FAILED);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    useEffect(() => {
        if (selectedHubs.length > 0) {

            // Only clear CP list, don't clear selected CPs for hub admin users
            if (hubUser !== "ROLE_HUBADMIN") {
                setCpList([]);
                setSelectedCPs([]);
            }
        }
    }, [selectedHubs, hubUser]);

    // Fetch CP list when hubs change
    useEffect(() => {
        if (!selectedHubs || selectedHubs.length === 0) {
            setCpList([]); // clear CP list if no hub selected
            setSelectedCPs([]); // Clear selected CPs when hub selection changes
            return;
        }

        (async () => {
            try {
                setCpLoading(true);
                const cpLists = await Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)));
                const allCps = [...new Set(cpLists.flat())];
                setCpList(allCps);
            } catch (error) {
                console.error("Failed to fetch CP list", error);
                message.error(REPORT_ERROR_MESSAGES.FETCH_CP_LIST_FAILED);
                setCpList([]);
            } finally {
                setCpLoading(false);
            }
        })();
    }, [selectedHubs]);

    // Debounced effect for report fetching - prevents rapid API calls during filter changes
    useEffect(() => {
        if (!hubUser) {
            return;
        }
        if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
            return;
        }

        // Debounce the API call to prevent multiple rapid calls when clearing filters
        const timer = setTimeout(() => {
            fetchReport();
        }, 300);

        return () => clearTimeout(timer);
    }, [hubUser, selectedHubs, selectedCPs, selectedZones, selectedBookingTypes, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {

        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning(REPORT_ERROR_MESSAGES.SELECT_DATE_RANGE);
            return;
        }

        // Create new request with cancellation support
        const { signal, requestId } = createNewRequest("ewaybill-report");

        setLoading(true);
        try {
            // Resolve Hubs (selectedHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const params = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                hubIds: resolvedHubs.length > 0 ? resolvedHubs : undefined,
                cpIds: selectedCPs.length > 0 ? selectedCPs : undefined,
                zones: selectedZones.length > 0 ? selectedZones : undefined,
                bookingTypes: selectedBookingTypes.length > 0 ? selectedBookingTypes : undefined,
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            const response = await ReportsService.getEwayBillReport(params, { signal });

            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring response");
                return;
            }

            if (response?.data) {
                setReportData(response.data);
                setLastsynced(response?.lastRefreshedAt);
                setNextScheduledRefresh(response?.nextScheduledRefresh);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0
                }));
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                message.error(REPORT_ERROR_MESSAGES.NO_DATA_AVAILABLE);
            }
        } catch (error) {
            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring error");
                return;
            }

            if (error.name === 'AbortError') {
                console.log("🔥 Request was cancelled");
                return;
            }

            console.error("Error fetching Eway Bill report:", error);
            message.error(error?.message || REPORT_ERROR_MESSAGES.FETCH_REPORT_FAILED);
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            // Only update loading state if this is still the latest request
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning(REPORT_ERROR_MESSAGES.SELECT_DATE_RANGE);
            return;
        }
        setDownloadLoading(true);
        try {
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const downloadParams = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                hubIds: resolvedHubs.length > 0 ? resolvedHubs : undefined,
                cpIds: selectedCPs.length > 0 ? selectedCPs : undefined,
                zones: selectedZones.length > 0 ? selectedZones : undefined,
                bookingTypes: selectedBookingTypes.length > 0 ? selectedBookingTypes : undefined
            };

            await downloadEwayBillReport(downloadParams);
            message.success(REPORT_DOWNLOAD_TEXT.SUCCESS);
        } catch (error) {
            console.error("Error downloading Eway Bill report:", error);
            message.error(REPORT_DOWNLOAD_TEXT.ERROR);
        } finally {
            setDownloadLoading(false);
        }
    };


    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleRowSelection = {
        selectedRowKeys: selectedRows,
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRowKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: !record.ewayBillNo, // Disable if no eway bill number
        }),
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
        },
        {
            title: "Eway Bill No",
            dataIndex: "ewayBillNo",
            key: "ewayBillNo",
            sorter: true,
            width: 150,
            render: (ewayBillNo) => ewayBillNo || "-"
        },
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            sorter: true,
            width: 150,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )

        },
        {
            title: "AWB Booking Date",
            dataIndex: "awbBookingDate",
            key: "awbBookingDate",
            sorter: true,
            width: 150,
            render: (awbBookingDate) => awbBookingDate || "-"
        },
        {
            title: "Eway Bill Generated Date",
            dataIndex: "ewayBillGeneratedDate",
            key: "ewayBillGeneratedDate",
            sorter: true,
            width: 180,
            render: (ewayBillGeneratedDate) => ewayBillGeneratedDate || "-"
        },
        {
            title: "Eway Bill Expiry Date",
            dataIndex: "ewayBillExpiryDate",
            key: "ewayBillExpiryDate",
            sorter: true,
            width: 180,
            render: (ewayBillExpiryDate) => ewayBillExpiryDate || "-"
        },
        {
            title: "Part B Updated Time",
            dataIndex: "partBUpdatedTime",
            key: "partBUpdatedTime",
            sorter: true,
            width: 180,
            render: (partBUpdatedTime) => partBUpdatedTime || "-"
        },
        {
            title: "Booking CP Name",
            dataIndex: "bookingCpName",
            key: "bookingCpName",
            sorter: true,
            width: 200,
            render: (bookingCpName) => bookingCpName || "-"
        },
        {
            title: "Booking Hub Name",
            dataIndex: "bookingHubName",
            key: "bookingHubName",
            sorter: true,
            width: 200,
            render: (bookingHubName) => bookingHubName || "-"
        },
        {
            title: "Part B Updated Hub Name",
            dataIndex: "partBUpdatedHub",
            key: "partBUpdatedHub",
            sorter: true,
            width: 200,
            render: (partBUpdatedHub) => partBUpdatedHub || "-"
        },
        {
            title: "Zone",
            dataIndex: "zone",
            key: "zone",
            sorter: true,
            width: 120,
            render: (zone) => zone || "-"
        },
        {
            title: "Current Location",
            dataIndex: "currentLocation",
            key: "currentLocation",
            sorter: true,
            width: 200,
            render: (currentLocation) => currentLocation || "-"
        }
    ];

    return (
        <div className="report-container" style={{ padding: "24px" }}>
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Eway Bill Report">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">{REPORT_FILTER_LABELS.DATE_RANGE}</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">{REPORT_FILTER_LABELS.HUBS}</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder={REPORT_PLACEHOLDERS.SELECT_HUBS}
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">{REPORT_FILTER_LABELS.CPS}</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder={cpLoading ? REPORT_PLACEHOLDERS.LOADING_CPS : cpList.length === 0 ? REPORT_PLACEHOLDERS.SELECT_HUB_FIRST : REPORT_PLACEHOLDERS.SELECT_CPS}
                            options={cpList}
                            onChange={(value) => {
                                setSelectedCPs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedCPs}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            disabled={cpLoading || cpList.length === 0}
                            loading={cpLoading}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">{REPORT_FILTER_LABELS.ZONES}</div>
                        <DropDownComponent
                            placeholder={REPORT_PLACEHOLDERS.SELECT_ZONES}
                            options={ZONE_OPTIONS}
                            onChange={(value) => {
                                setSelectedZones(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedZones}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Types</div>
                        <DropDownComponent
                            placeholder="Select Booking Types"
                            options={BOOKING_TYPE_OPTIONS}
                            onChange={(value) => {
                                setSelectedBookingTypes(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedBookingTypes}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                </Row>

                <Row justify="end" className="download-row" gutter={[8, 0]}>
                    <Col>
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            loading={downloadLoading}
                            icon={<DownloadOutlined />}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                            className="download-button"
                        >
                            {REPORT_DOWNLOAD_TEXT.BUTTON}
                        </Button>
                    </Col>
                </Row>

                <div className="table-section">
                    <TableComponent
                        columns={columns}
                        dataSource={reportData}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        scroll={{ x: 1500 }}
                        rowKey="ewayBillNo"
                        rowSelection={handleRowSelection}
                    />
                </div>
            </Card>
        </div>
    );
};

export default EwayBillReport;
